import React, { useState, useRef, useEffect } from "react";
import {
  Typography, Table, TableHead, TableRow, TableCell, Box,
  TableBody, IconButton, Dialog, DialogTitle, DialogContent, Button
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DutTestpoint } from "../../models/signalSources/dutTestpoint.ts";
import EditDutTestpointFormView from "./EditDutTestpointForm.tsx";
import { useCsvToDutTestpoints } from "../../hooks/useCsvToDutTestpoints.ts";
import { v4 as uuidv4 } from 'uuid';
import { useMessageHandler } from "../../context/MessageContext.tsx";
import FieldWithTrailingIcon from "../../components/FieldWithTrailingIcon.tsx"
import InfoDialog from "../../components/InfoDialog.tsx"; 
import {Grid, TipStyle} from "../../models/contact.ts"
import { useStandardContact } from "../../hooks/useStandardContact.ts"

interface Props {
  data: DutTestpoint[];
  onChange: (newList: DutTestpoint[]) => void;
}

const DutTestpointTable: React.FC<Props> = ({ data, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConentIsValid, setDialogConentIsValid] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [altiumInfoOpen, setAltiumInfoOpen] = useState(false);
  const [emptyTestpoint, setEmptyTestpoint] = useState<DutTestpoint>({
    name: "",
    net: "",
    side: "Top",
    contact: {
      description: "",
      id: "",
      grid: Grid.MIL100,
      needle: {
        current: 0,
        diameter: 0,
        diameter_spacing: 0,
        image_name: "",
        order_number: "",
        sk_number: "",
        supplier: "",
        tip_style: TipStyle.SMALL_SINGLE_POINT,
      },
      sleeve: {
        diameter: 0,
        diameter_spacing: 0,
        order_number: "",
        sk_number: "",
        supplier: "",
      }
    },
    unit: "mm",
    x: 0,
    y: 0,
    id: ""
  });
  const [editTestpoint, setEditTestpoint] = useState<DutTestpoint>(emptyTestpoint);
  const { upload: uploadCsv } = useCsvToDutTestpoints();
  const { setError } = useMessageHandler();
  const { option: standardContact } = useStandardContact();

  const validate = () => {
    if (data.length) {
      const allSameSide = data.every(tp => tp.side === data[0].side);

      const seen = new Set<string>();
      const hasDuplicateNameNet = data.some(tp => {
        const key = `${tp.name}__${tp.net}`;
        if (seen.has(key)) return true;
        seen.add(key);
        return false;
      });

      let message = "";
      if (data.length > 100) message += `Es sind maximal 100 Testpunkte erlaubt (aktuell ${data.length}). `
      if (!allSameSide) message += "Alle Testpunkte müssen auf derselben Seite liegen. ";
      if (hasDuplicateNameNet) message += "Name und Netz dürfen bei Testpunkten in Kombination nicht doppelt vorkommen. ";
      if (message) setError(message.trim());
    }
  }

  useEffect(() => {
    if (standardContact) {
      setEmptyTestpoint(prev => ({
        ...prev,
        contact: standardContact,
      }));
      setEditTestpoint(prev => ({
        ...prev,
        contact: standardContact,
      }));
    }
  }, [standardContact]);

  useEffect(() => {
    validate();
  }, [data]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadCsv(file);
    if (result) {
      onChange([...data, ...result]);
    }  
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChange = (field: keyof DutTestpoint, value: any) => {
    setEditTestpoint({ ...editTestpoint, [field]: value });
  };

  const onAdd = () => {
    setEditIndex(null);
    let tp = emptyTestpoint;
    tp.id = uuidv4();
    setEditTestpoint(tp);
    setDialogOpen(true);
  }

  const onEdit = (index: number) => {
    setEditIndex(index);
    setEditTestpoint(data[index]);
    setDialogOpen(true);
  };

  const onDelete = (index: number, count: number) => {
    let newData = data;
    newData.splice(index, count)
    onChange(newData);
    validate();
  }

  const onSave = () => {
    const updated = [...data];
    if (editIndex !== null) {
      updated[editIndex] = editTestpoint;
    } else {
      updated.push(editTestpoint);
    }
    onChange(updated);
    onClose();
  };

  const onClose = () => {
    setDialogOpen(false);
    setDialogConentIsValid(false);
    setEditIndex(null);
    setEditTestpoint(emptyTestpoint);
    validate();
  };

  return (
    <>
        <Typography variant="h6" gutterBottom>
          Testpunkte
        </Typography>
        {data.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Netz</TableCell>
                <TableCell>X Koordinate</TableCell>
                <TableCell>Y Koordinate</TableCell>
                <TableCell>Einheit</TableCell>
                <TableCell>Layer</TableCell>
                <TableCell>Kontakt</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((tp, index) => (
                <TableRow key={index}>
                  <TableCell>{tp.name}</TableCell>
                  <TableCell>{tp.net}</TableCell>
                  <TableCell>{tp.x}</TableCell>
                  <TableCell>{tp.y}</TableCell>
                  <TableCell>{tp.unit}</TableCell>
                  <TableCell>{tp.side}</TableCell>
                  <TableCell>{tp.contact.description}</TableCell>
                  <TableCell>
                    <IconButton size="small" color="primary" onClick={() => onEdit(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => onDelete(index, 1)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Box mt={2} />
        <Box mt={2} display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={onAdd}
          >
            Hinzufügen
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => onDelete(0, data.length)}
          >
            Zurücksetzen
          </Button>
          <FieldWithTrailingIcon
            icon={<InfoOutlinedIcon fontSize="small" />}
            onIconClick={() => setAltiumInfoOpen(true)}
          >
          <Button
            variant="outlined"
            color="primary"
            component="label"
          >
            Upload Altium Testpoint Report (csv)
            <input type="file" accept=".csv" hidden onChange={handleFileChange} ref={fileInputRef} />
          </Button>
          </FieldWithTrailingIcon>
        </Box>
      <Dialog open={dialogOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editIndex === null ? "Neuen Testpunkt hinzufügen" : "Testpunkt bearbeiten"}
        </DialogTitle>
        <DialogContent>
          <EditDutTestpointFormView data={editTestpoint} setIsValid={setDialogConentIsValid} onChange={handleChange} />
          <Box mt={2} display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={!dialogConentIsValid}
              sx={{ mt: 2 }}
            >
              Speichern
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ mt: 2 }}
            >
              Abbrechen
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <InfoDialog
        open={altiumInfoOpen}
        onClose={() => setAltiumInfoOpen(false)}
        title="Upload Altium Testpoint Report (csv)"
        text={`In den Altium kann direkt aus dem Design heraus ein Tespoint-Report erstellt werden.
        Diese Datei kann direkt in den qdTest Konfigurator eingelesen werden.
        Auszug aus einer Beispiel-Datei:
        "Net","Name","X Coord","Y Coord","Side","Hole Size","Type"
        "VEXT","X1-10","18.67mm","80.3mm","Bottom","1.2mm","Through hole"
        "GND","X1-9","22.48mm","80.3mm","Bottom","1.2mm","Through hole"
        
        Weiterführende Informationen: https://www.altium.com/documentation/altium-designer/adding-testpoints-pcb`}
      />
    </>
  );
};

export default DutTestpointTable;